import { useState } from "react";
import { Box, PlayIcon, HtImage } from "@hightouch/htui";

const YOUTUBE_THUMBNAIL_PATTERN =
  /^https:\/\/img\.youtube\.com\/vi\/([A-Za-z0-9_-]+)\//;

/** The id segment of a youtube_video row's thumbnail URL. */
export function youtubeVideoIdFromThumbnailUrl(
  mediaUrl: string | null | undefined,
): string | null {
  if (!mediaUrl) return null;
  return YOUTUBE_THUMBNAIL_PATTERN.exec(mediaUrl)?.[1] ?? null;
}

/** Whether a click on the player stays in it, or reaches a selectable card it sits on. */
export type YouTubeClicks = "stay-in-player" | "reach-card";

type YouTubeEmbedProps = {
  videoId: string;
  thumbnailUrl: string;
  /** CSS aspect-ratio of the reserved box; ignored when height is set. */
  aspectRatio?: string;
  width?: string;
  height?: string;
  borderRadius?: string;
};

export const YouTubeEmbed = ({
  videoId,
  thumbnailUrl,
  aspectRatio,
  width,
  height,
  borderRadius,
}: YouTubeEmbedProps) => (
  <YouTubePlayer
    clicks="stay-in-player"
    videoId={videoId}
    thumbnailUrl={thumbnailUrl}
    aspectRatio={aspectRatio}
    width={width}
    height={height}
    borderRadius={borderRadius}
  />
);

/** Click-to-play YouTube player: thumbnail with a play badge, iframe once
 * clicked. Grids hold dozens of these, so the player only mounts on demand. */
export function YouTubePlayer({
  videoId,
  clicks,
  thumbnailUrl,
  aspectRatio = "1 / 1",
  width = "100%",
  height,
  borderRadius,
}: YouTubeEmbedProps & { clicks: YouTubeClicks }) {
  const [playing, setPlaying] = useState(false);

  const ratio = height ? undefined : { aspectRatio };

  if (playing) {
    return (
      <Box
        as="iframe"
        src={`https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1`}
        title="YouTube video"
        allow="autoplay; encrypted-media; picture-in-picture"
        allowFullScreen
        referrerPolicy="strict-origin-when-cross-origin"
        border="none"
        borderRadius={borderRadius}
        bg="base.border"
        pointerEvents={clicks === "reach-card" ? "none" : "auto"}
        tabIndex={clicks === "reach-card" ? -1 : undefined}
        // Keeps clicks around the player's edges from triggering card
        // navigation.
        onClick={(event: React.MouseEvent) => event.stopPropagation()}
        width={width}
        height={height}
        sx={ratio}
      />
    );
  }

  return (
    <Box
      position="relative"
      cursor="pointer"
      overflow="hidden"
      borderRadius={borderRadius}
      onClick={(event: React.MouseEvent) => {
        if (clicks === "stay-in-player") {
          event.stopPropagation();
        }
        setPlaying(true);
      }}
      width={width}
      height={height}
      sx={ratio}
    >
      <HtImage
        src={thumbnailUrl}
        alt="YouTube video thumbnail"
        width="100%"
        height="100%"
        objectFit="cover"
        bg="base.border"
      />
      <Box
        position="absolute"
        top={0}
        right={0}
        bottom={0}
        left={0}
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          width="48px"
          height="48px"
          borderRadius="full"
          bg="blackAlpha.60"
        >
          <Box as={PlayIcon} color="white" width="24px" height="24px" />
        </Box>
      </Box>
    </Box>
  );
}
